import { CrownFrame } from "@/components/CrownFrame";
import MwpBoard from "@/components/MwpBoard";
import { WolfLink } from "@/pages/WolfPage";
import WolfSearch from "@/components/WolfSearch";
import { useEffect, useState } from "react";
import { Crown, Trophy, Swords, HeartPulse } from "lucide-react";
import { api } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import HallOfFame from "@/components/HallOfFame";
import { DropBadges } from "@/components/GoldBadge";
import { MwpBanner } from "@/components/MwpBanner";
import HolderBoard from "@/components/HolderBoard";

const COLL = { c1: "Exclusive", c2: "Wolfpack", c3: "PUPS" };
const MEDAL = ["text-amber-300", "text-slate-200", "text-orange-300"];

function Row({ e, me, children, testid }) {
  return (
    <div data-testid={testid} className={`flex items-center gap-4 p-3 sm:p-4 rounded-2xl border transition-colors ${me ? "border-fuchsia-500/60 bg-fuchsia-500/10" : "border-slate-800 bg-[#081820]/70 hover:border-slate-600"}`}>
      <span className={`relative w-8 text-center font-display text-2xl ${MEDAL[e.rank - 1] || "text-slate-500"}`}>
        {e.crown && <Crown data-testid={`lb-crown-${e.crown}`} className={`absolute -top-4 left-1/2 -translate-x-1/2 w-5 h-5 ${MEDAL[e.crown - 1]} drop-shadow-[0_0_6px_rgba(250,204,21,.7)]`} />}
        {e.rank}
      </span>
      <CrownFrame tier={e.mwp_tier} small className="rounded-xl"><img src={e.portrait} alt={e.wolf_name} loading="lazy" className={`w-14 h-14 rounded-xl object-cover border ${e.character ? "border-amber-400/70 nft-glow" : "border-slate-700"}`}
        onError={(ev) => { ev.currentTarget.src = "/art/frost.webp"; }} /></CrownFrame>
      <div className="flex-1 min-w-0">
        <p className="font-display text-lg text-white flex items-center gap-2 min-w-0"><WolfLink name={e.wolf_name} testid={`${testid}-link`} className="truncate min-w-0" /><DropBadges o={e} testid={testid} />{me && <span className="shrink-0 text-xs font-sans text-fuchsia-300">you</span>}</p>
        <p className="text-xs text-slate-400 truncate">{e.character ? `as ${e.character}` : "Stock wolf"}</p>
        {e.holdings?.length > 0 && (
          <div className="flex gap-1 mt-1">{e.holdings.map((h) => <span key={h} className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-200 text-[10px] font-mono-game">{COLL[h]}</span>)}</div>
        )}
      </div>
      {children}
    </div>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="text-right">
      <p className="font-mono-game text-lg text-white flex items-center justify-end gap-1"><Icon className="w-4 h-4 text-fuchsia-300" />{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
    </div>
  );
}

function BossList({ b, me, prefix, weekly }) {
  return (
    <TabsContent value={b.id} className="space-y-3" data-testid={`lb-${prefix}${b.id}-list`}>
      <div className="flex items-center gap-4 mb-4">
        <img src={b.image} alt={b.name} className="w-16 h-16 rounded-xl object-cover" />
        <p className="text-sm text-slate-400">Fewest turns to defeat {b.name}{weekly ? " this week" : ""}. Ties go to fewer revives.</p>
      </div>
      {b.entries.length === 0 && <p data-testid={`lb-${prefix}${b.id}-empty`} className="text-slate-500 py-8 text-center">No one has defeated {b.name.split(",")[0]} {weekly ? "this week" : "yet"}. Be the first.</p>}
      {b.entries.map((e) => (
        <Row key={e.rank} e={e} me={me(e)} testid={`lb-${prefix}${b.id}-row-${e.rank}`}>
          <Stat icon={Swords} value={e.turns} label="Turns" />
          <Stat icon={HeartPulse} value={e.revives_used} label="Revives" />
        </Row>
      ))}
    </TabsContent>
  );
}

function resetIn(iso) {
  const ms = new Date(iso) - Date.now();
  const d = Math.floor(ms / 864e5), h = Math.floor((ms % 864e5) / 36e5);
  return d > 0 ? `${d}d ${h}h` : `${h}h`;
}

export default function Leaderboard() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [mode, setMode] = useState("weekly");
  useEffect(() => { api.get("/leaderboard").then(({ data }) => setD(data)).catch(() => setErr(true)); }, []);
  const me = (e) => e.is_me;
  const weekly = mode === "weekly";

  return (
    <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
      <p className="text-xs uppercase tracking-[0.3em] font-mono-game text-amber-400/90 mb-3">Hall of the Pack</p>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <h1 className="font-display text-4xl sm:text-5xl text-white">Leaderboard</h1>
        <WolfSearch />
      </div>
      {d?.mwp && <MwpBanner mwp={d.mwp} />}
      <div className="inline-flex p-1 rounded-full bg-slate-900 border border-slate-800 mb-4" role="tablist">
        {[["weekly", "This week"], ["alltime", "All time"], ["holders", "Top Holders"], ["mwp", "$MWP Holders"], ["fame", "Hall of Fame"]].map(([k, l]) => (
          <button key={k} data-testid={`lb-mode-${k}`} onClick={() => setMode(k)} aria-selected={mode === k}
            className={`px-5 py-2 rounded-full text-sm transition-colors ${mode === k ? "ember-btn" : "text-slate-400 hover:text-white"}`}>{l}</button>
        ))}
      </div>
      <p data-testid="lb-champion-note" className="text-sm text-slate-400 mb-10 flex items-start gap-2">
        <Crown className="w-4 h-4 mt-0.5 shrink-0 text-amber-300" />
        {weekly
          ? <span>Weekly boards reset every Monday{d ? ` (in ${resetIn(d.resets_at)})` : ""}. The top 3 on each win 5,000, 3,000 and 1,500 credits and wear a crown for the next week{d?.champion_week ? `. Current crowns: ${d.champion_week}` : ""}.</span>
          : mode === "fame" ? <span>Every weekly champion, with their crown and the week they won it.</span>
          : mode === "mwp" ? <span>Players holding the most $MWP on Base in their linked wallet. Bronze any amount, Silver 250K, Gold 1M, Platinum 10M; each tier adds 2% to 15% to credits earned, and Platinum wears a crown frame.</span>
          : mode === "holders" ? <span>Players holding the most 1/1 Wolf NFTs in their Solana wallet. Link that wallet on NFT Boosts and each wolf held adds +5% to every stat, up to +25%.</span>
          : <span>All-time records: who has climbed furthest toward Moonfang Peak, and the fastest boss kills ever.</span>}
      </p>
      {mode === "fame" ? <HallOfFame /> : mode === "holders" ? <HolderBoard /> : mode === "mwp" ? <MwpBoard /> : err ? <p data-testid="lb-error" className="text-red-300">Couldn't load the leaderboard. Please refresh to try again.</p> : !d ? <p className="text-slate-500">Counting the pack…</p> : weekly ? (
        <Tabs key="weekly" defaultValue="regions">
          <TabsList className="bg-slate-900 mb-6 flex-wrap h-auto">
            <TabsTrigger value="regions" data-testid="lb-tab-week-regions"><Trophy className="w-4 h-4 mr-1.5" />Most regions cleared</TabsTrigger>
            {d.weekly.bosses.map((b) => <TabsTrigger key={b.id} value={b.id} data-testid={`lb-tab-week-${b.id}`}><Crown className="w-4 h-4 mr-1.5" />Fastest {b.name.split(",")[0]}</TabsTrigger>)}
          </TabsList>
          <TabsContent value="regions" className="space-y-3" data-testid="lb-week-regions-list">
            {d.weekly.regions.length === 0 && <p data-testid="lb-week-regions-empty" className="text-slate-500 py-8 text-center">No regions cleared yet this week. The board is wide open.</p>}
            {d.weekly.regions.map((e) => (
              <Row key={e.rank} e={e} me={me(e)} testid={`lb-week-regions-row-${e.rank}`}>
                <Stat icon={Trophy} value={e.regions_this_week} label="This week" />
                <Stat icon={Swords} value={e.furthest} label="Furthest" />
              </Row>
            ))}
          </TabsContent>
          {d.weekly.bosses.map((b) => <BossList key={b.id} b={b} me={me} prefix="week-" weekly />)}
        </Tabs>
      ) : (
        <Tabs key="alltime" defaultValue="strongest">
          <TabsList className="bg-slate-900 mb-6 flex-wrap h-auto">
            <TabsTrigger value="strongest" data-testid="lb-tab-strongest"><Trophy className="w-4 h-4 mr-1.5" />Furthest progressed</TabsTrigger>
            {d.alltime.bosses.map((b) => <TabsTrigger key={b.id} value={b.id} data-testid={`lb-tab-${b.id}`}><Crown className="w-4 h-4 mr-1.5" />Fastest {b.name.split(",")[0]} ever</TabsTrigger>)}
          </TabsList>
          <TabsContent value="strongest" className="space-y-3" data-testid="lb-strongest-list">
            {d.alltime.strongest.map((e) => (
              <Row key={e.rank} e={e} me={me(e)} testid={`lb-strongest-row-${e.rank}`}>
                <Stat icon={Trophy} value={e.regions_cleared} label="Regions" />
                <Stat icon={Swords} value={e.level} label="Level" />
              </Row>
            ))}
          </TabsContent>
          {d.alltime.bosses.map((b) => <BossList key={b.id} b={b} me={me} prefix="" />)}
        </Tabs>
      )}
    </main>
  );
}
